import { queryOptions } from '@tanstack/react-query'
import type { Database } from '@/types/supabase'
import { edgeFunctionErrorMessage } from '@/lib/generation/shared/edge-function-error'
import type { BillingInterval } from '@/lib/pricing'
import { supabase } from '@/lib/supabase/client'

export type CreditPack = Database['public']['Tables']['credit_packs']['Row']
export type CreditPackPrice =
  Database['public']['Tables']['credit_pack_prices']['Row']

/** A pack as one tier buys it. */
export interface CreditPackOffer extends CreditPack {
  price_cents: number | null
  currency: string
}

export interface CreditPackWithPrices extends CreditPack {
  credit_pack_prices: CreditPackPrice[]
}

export const creditPacks = {
  /** What an account on `planCode` can buy, at that tier's price. */
  findManyByPlan: (planCode: string) =>
    queryOptions({
      queryKey: ['credit-packs', 'plan', planCode],
      queryFn: async (): Promise<CreditPackOffer[]> => {
        if (!planCode || planCode.trim() === '') {
          throw new Error('Invalid planCode: cannot price credit packs')
        }
        const { data, error } = await supabase
          .from('credit_packs')
          .select('*, credit_pack_prices!inner(price_cents, currency)')
          .eq('credit_pack_prices.plan_code', planCode)
          .order('sort_order')
        if (error) {
          throw new Error(`Failed to fetch credit packs: ${error.message}`, {
            cause: error,
          })
        }
        return (data ?? []).map(({ credit_pack_prices: [price], ...pack }) => ({
          ...pack,
          price_cents: price.price_cents,
          currency: price.currency,
        }))
      },
      staleTime: 10 * 60 * 1000,
    }),

  /** Every pack with its price on every tier, for the admin catalogue. */
  all: () =>
    queryOptions({
      queryKey: ['credit-packs', 'all'],
      queryFn: async (): Promise<CreditPackWithPrices[]> => {
        const { data, error } = await supabase
          .from('credit_packs')
          .select('*, credit_pack_prices(*)')
          .order('sort_order')
        if (error) {
          throw new Error(`Failed to fetch credit packs: ${error.message}`, {
            cause: error,
          })
        }
        return data ?? []
      },
      staleTime: 10 * 60 * 1000,
    }),
}

function billingPath(orgId: string): string {
  return `/org/${orgId}/billing`
}

/**
 * Hands the browser to Stripe's hosted Checkout. The edge function decides
 * whether this account may buy (self-serve, not already subscribed for a plan)
 * and answers 409 with the reason otherwise, which surfaces as the toast.
 */
export async function startCheckout(input: {
  orgId: string
  kind: 'plan' | 'pack'
  code: string
  interval?: BillingInterval
}): Promise<void> {
  const { data, error } = await supabase.functions.invoke<{ url: string }>(
    'stripe-checkout',
    {
      body: {
        org_id: input.orgId,
        kind: input.kind,
        code: input.code,
        interval: input.interval,
        origin: window.location.origin,
        return_path: billingPath(input.orgId),
      },
    }
  )
  if (error) {
    throw new Error(
      await edgeFunctionErrorMessage(error, 'Could not start checkout.'),
      {
        cause: error,
      }
    )
  }
  if (!data?.url) {
    throw new Error('Checkout did not return a URL')
  }
  window.location.assign(data.url)
}

/**
 * Hands the browser to Stripe's customer portal for card, invoices, cancel and
 * downgrades. `flow` deep-links to one task, e.g. fixing a failed card.
 */
export async function openBillingPortal(
  orgId: string,
  flow?: 'payment_method_update'
): Promise<void> {
  const { data, error } = await supabase.functions.invoke<{ url: string }>(
    'stripe-portal',
    {
      body: {
        org_id: orgId,
        origin: window.location.origin,
        return_path: billingPath(orgId),
        flow,
      },
    }
  )
  if (error) {
    throw new Error(
      await edgeFunctionErrorMessage(error, 'Could not open billing.'),
      {
        cause: error,
      }
    )
  }
  if (!data?.url) {
    throw new Error('Billing portal did not return a URL')
  }
  window.location.assign(data.url)
}

export type CheckoutStatus = 'paid' | 'processing' | 'open' | 'expired'

export interface CheckoutStatusResult {
  status: CheckoutStatus
  kind: 'plan' | 'pack'
}

export const checkoutStatus = (orgId: string, sessionId: string) =>
  queryOptions({
    queryKey: ['stripe', 'checkout-status', sessionId],
    /**
     * Stripe's landing-page step: the server reads the session, fulfils it
     * if it is paid (the same idempotent path as the webhook) and says where
     * it stands. `processing` is a delayed payment method still clearing.
     */
    queryFn: async (): Promise<CheckoutStatusResult> => {
      const { data, error } =
        await supabase.functions.invoke<CheckoutStatusResult>(
          'stripe-checkout-status',
          { body: { org_id: orgId, session_id: sessionId } }
        )
      if (error) {
        throw new Error(
          await edgeFunctionErrorMessage(
            error,
            'Could not confirm the payment.'
          ),
          { cause: error }
        )
      }
      if (!data) {
        throw new Error('No result returned')
      }
      return data
    },
    staleTime: Infinity,
  })

/**
 * Whether the webhook has already applied this Checkout, read straight from
 * the database: no edge function in the way, so the return page can say
 * "done" the moment it is. A pack is done when its purchase row exists (the
 * webhook tags it with the session); a plan bought from Free is done when the
 * account's subscription is Stripe's.
 */
export const checkoutLanded = (
  sessionId: string,
  kind: 'plan' | 'pack',
  billingAccountId: string
) =>
  queryOptions({
    queryKey: ['stripe', 'checkout-landed', sessionId],
    queryFn: async (): Promise<boolean> => {
      if (kind === 'pack') {
        const { data, error } = await supabase
          .from('credit_ledger')
          .select('id')
          .eq('action_code', 'credit.purchase')
          .eq('metadata->>checkout_session', sessionId)
          .limit(1)
        if (error) {
          throw error
        }
        return data.length > 0
      }
      const { data, error } = await supabase.rpc('get_entitlements', {
        p_billing_account_id: billingAccountId,
      })
      if (error) {
        throw error
      }
      return data?.[0]?.stripe_managed === true
    },
    staleTime: 0,
  })

export type PlanChangeMode = 'upgrade' | 'downgrade' | 'cancel'

export interface PlanChangePreview {
  mode: PlanChangeMode
  /** Charged now, VAT included — upgrades only; 0 for changes that wait for the period end. */
  due_today_cents: number
  /** The VAT inside due_today_cents; 0 when none applies. */
  tax_today_cents: number
  currency: string
  recurring_cents: number | null
  interval: BillingInterval
  plan_name: string
  /** When a downgrade or cancellation takes effect. */
  effective_at: string | null
}

async function invokeChangePlan<T>(
  body: Record<string, unknown>,
  fallback: string
): Promise<T> {
  const { data, error } = await supabase.functions.invoke<T>(
    'stripe-change-plan',
    { body: { ...body, origin: window.location.origin } }
  )
  if (error) {
    throw new Error(await edgeFunctionErrorMessage(error, fallback), {
      cause: error,
    })
  }
  if (!data) {
    throw new Error('No result returned')
  }
  return data
}

/** What moving the live subscription to `code` would do, and cost, today. */
export async function previewPlanChange(input: {
  orgId: string
  code: string
  interval?: BillingInterval
}): Promise<PlanChangePreview> {
  return invokeChangePlan<PlanChangePreview>(
    {
      action: 'preview',
      org_id: input.orgId,
      code: input.code,
      interval: input.interval,
      return_path: billingPath(input.orgId),
    },
    'Could not price the change.'
  )
}

/**
 * Applies the change: an upgrade now (charged now), a downgrade or Free at the
 * period end. `{ url }` means the card needs the customer, and the browser
 * should go there.
 */
export async function changePlan(input: {
  orgId: string
  code: string
  interval?: BillingInterval
}): Promise<{ mode: PlanChangeMode; done: boolean; url?: string }> {
  return invokeChangePlan(
    {
      action: 'change',
      org_id: input.orgId,
      code: input.code,
      interval: input.interval,
      return_path: billingPath(input.orgId),
    },
    'Could not change the plan.'
  )
}

/** Undoes a pending downgrade or cancellation; the current plan simply continues. */
export async function revertPlanChange(orgId: string): Promise<void> {
  await invokeChangePlan(
    { action: 'revert', org_id: orgId, return_path: billingPath(orgId) },
    'Could not keep the plan.'
  )
}
